import React from "react";
import { HOUSING_INCOME_ROWS, RENTAL_CHECKLIST, RENTAL_STEPS } from "../../content/housingProgramGuideData";
import HousingProgramGuideAudience from "./HousingProgramGuideAudience";
import { GuideSection, guideBodyClass } from "./guideSection";

export default function HousingProgramGuide() {
  return (
    <div className="flex flex-col gap-8">
      <p className={guideBodyClass}>
        Se você encontrou um apartamento classificado como HIS, HIS-1, HIS-2 ou HMP, existem
        regras específicas sobre quem pode morar nele, renda familiar, valor de aluguel e forma de
        utilização. A 7Cantos é especializada em locação de imóveis desse formato.
      </p>

      <GuideSection id="o-que-significa" title="O que significa HIS e HMP?">
        <p className={guideBodyClass}>
          HIS significa Habitação de Interesse Social e é destinada a famílias de menor renda. HMP
          significa Habitação de Mercado Popular e atende famílias com renda intermediária.
        </p>
        <p className={guideBodyClass}>
          Em São Paulo, essas classificações fazem parte da política habitacional municipal e
          determinam quem pode ocupar o imóvel, os limites de renda e as regras para venda e
          locação.
        </p>
      </GuideSection>

      <GuideSection id="tabela" title="Qual a diferença entre HIS-1, HIS-2 e HMP?">
        <p className={guideBodyClass}>
          Os valores abaixo são os limites de renda e aluguel vigentes para 2026 em São Paulo.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm text-[#3d4754]">
            <thead>
              <tr className="border-b border-[#eadfd6]">
                {["Categoria", "Significado", "Renda familiar máxima", "Renda per capita máxima", "Limite de aluguel"].map(
                  (header) => (
                    <th key={header} className="px-3 py-2 font-semibold text-[#1f2933]">
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {HOUSING_INCOME_ROWS.map((row) => (
                <tr key={row.category} className="border-b border-[#eadfd6]">
                  <th className="px-3 py-2 font-semibold text-[#1f2933]">{row.category}</th>
                  <td className="px-3 py-2">{row.meaning}</td>
                  <td className="px-3 py-2">{row.familyIncome}</td>
                  <td className="px-3 py-2">{row.perCapitaIncome}</td>
                  <td className="px-3 py-2">{row.rentLimit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={guideBodyClass}>
          A regulamentação municipal estabelece que o valor da locação deve respeitar o limite de
          30% da renda familiar máxima prevista para cada tipologia. A análise da unidade deve
          considerar a legislação aplicável ao caso concreto.
        </p>
      </GuideSection>

      <GuideSection id="o-que-e-his" title="O que é um imóvel HIS?">
        <p className={guideBodyClass}>
          São imóveis inseridos na política habitacional do município para ampliar o acesso à
          moradia de famílias de menor renda. Em troca de incentivos urbanísticos, essas unidades
          permanecem vinculadas a regras específicas de destinação. Um apartamento HIS não é apenas
          um apartamento mais barato: a classificação determina quem pode comprar ou alugar a
          unidade durante o período estabelecido pela legislação.
        </p>
        <p className={guideBodyClass}>
          Em 2026, HIS-1 atende famílias com renda mensal de até R$ 4.863,00, ou renda per capita de
          até R$ 810,50. HIS-2 atende famílias com renda mensal de até R$ 9.726,00, ou renda per
          capita de até R$ 1.621,00. A classificação não é determinada pelo tamanho, pelo número de
          dormitórios ou pelo bairro. Ela decorre da legislação e do projeto aprovado.
        </p>
      </GuideSection>

      <GuideSection id="o-que-e-hmp" title="O que significa HMP?">
        <p className={guideBodyClass}>
          Habitação de Mercado Popular atende famílias de renda intermediária e complementa a
          Habitação de Interesse Social. Em 2026, a renda familiar mensal máxima é de R$ 16.210,00,
          ou renda per capita mensal máxima de R$ 2.431,50. Imóveis HMP também têm regras próprias
          de destinação, comercialização e locação.
        </p>
        <p className={guideBodyClass}>
          HIS ou HMP não significam baixa qualidade. Há unidades em empreendimentos modernos,
          próximos a metrô, centros empresariais e regiões valorizadas. A classificação trata da
          política habitacional e da destinação da unidade, não do padrão arquitetônico.
        </p>
      </GuideSection>

      <GuideSection id="pode-alugar" title="É possível alugar um imóvel HIS ou HMP?">
        <p className={guideBodyClass}>
          Sim. Imóveis HIS e HMP podem ser alugados em São Paulo, desde que sejam respeitadas as
          regras da categoria. O locatário precisa estar enquadrado na faixa de renda correspondente
          ao imóvel. Antes da assinatura, é necessário verificar:
        </p>
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-[#3d4754]">
          {RENTAL_CHECKLIST.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </GuideSection>

      <GuideSection id="como-funciona" title="Como alugar um imóvel HIS ou HMP?">
        <ol className="m-0 flex list-decimal flex-col gap-3 pl-5 text-sm text-[#3d4754]">
          {RENTAL_STEPS.map((step) => (
            <li key={step.title}>
              <span className="font-semibold text-[#1f2933]">{step.title}.</span> {step.body}
            </li>
          ))}
        </ol>
      </GuideSection>

      <GuideSection id="documentacao" title="Quais documentos podem ser necessários?">
        <p className={guideBodyClass}>
          A análise pode exigir documentos adicionais de comprovação de renda, documentos pessoais e
          outras evidências conforme o perfil profissional e familiar. A documentação varia de
          acordo com a renda e as condições da unidade. A equipe da 7Cantos orienta o que é
          necessário em cada processo.
        </p>
      </GuideSection>

      <GuideSection id="airbnb" title="Pode colocar um imóvel HIS ou HMP no Airbnb?">
        <p className={guideBodyClass}>
          Não. A legislação municipal de São Paulo proíbe a locação de curta temporada em unidades
          HIS-1, HIS-2 e HMP, inclusive hospedagens de curta duração em plataformas digitais. Para
          gerar renda, a locação residencial dentro das regras aplicáveis é o caminho previsto.
        </p>
      </GuideSection>

      <GuideSection id="prazo" title="Por quanto tempo as regras precisam ser respeitadas?">
        <p className={guideBodyClass}>
          Durante 10 anos, contados da primeira comercialização da unidade ou da emissão do
          Habite-se, conforme a situação prevista na regulamentação, a unidade deve permanecer
          destinada ao público da sua classificação. Nesse período, venda e locação respeitam as
          condições de HIS ou HMP. Comprar o imóvel não o transforma automaticamente em unidade de
          mercado livre.
        </p>
      </GuideSection>

      <HousingProgramGuideAudience />
    </div>
  );
}
